// Tiny in-memory read cache for API GETs (PERFORMANCE_PLAN.md #5).
//
// Two jobs:
//   1. Coalesce concurrent identical requests into a single in-flight fetch
//      (e.g. the Shell and a pane asking for the same data on load).
//   2. Serve a recent response within a short TTL so rapid navigation doesn't
//      refetch the same data — the perf logs showed /events hit 16× and
//      /suggestions 6× just from clicking around.
//
// Correctness model (deliberately simple): any successful write (non-GET) clears
// the whole cache — see `call()` in api.ts. A mutation therefore can never leave a
// stale read behind; at worst it forces the next read to refetch. Writes are rare
// and user-initiated, so this costs us nothing on the hot navigation path.
//
// Only opt-in callers are cached (they pass a TTL); polling GETs (import/job status)
// stay uncached and keep their live behavior.

interface Entry {
  value: unknown;
  fetchedAt: number;
  /** Shared promise while a fetch is in flight, so concurrent callers coalesce. */
  inflight?: Promise<unknown>;
}

const store = new Map<string, Entry>();

/**
 * Returns a cached value if a fresh one exists, joins an in-flight fetch if one is
 * running, otherwise fetches and caches. `key` should uniquely identify the request
 * (the full path + query string works well).
 */
export async function cachedRead<T>(key: string, ttlMs: number, fetcher: () => Promise<T>): Promise<T> {
  const now = Date.now();
  const hit = store.get(key);
  if (hit) {
    if (hit.inflight) return hit.inflight as Promise<T>;      // coalesce concurrent callers
    if (now - hit.fetchedAt < ttlMs) return hit.value as T;   // fresh enough → serve from cache
  }

  let entry: Entry | undefined;
  const promise = (async () => {
    try {
      const value = await fetcher();
      store.set(key, { value, fetchedAt: Date.now() });
      return value;
    } catch (err) {
      // Drop the in-flight marker so the next call retries instead of resolving stale —
      // but only if our entry is still the current one (a write may have cleared it).
      if (store.get(key) === entry) store.delete(key);
      throw err;
    }
  })();

  // Preserve any prior value so a concurrent fresh-check still works, but mark in-flight.
  entry = { value: hit?.value, fetchedAt: hit?.fetchedAt ?? 0, inflight: promise };
  store.set(key, entry);
  return promise;
}

/** Wipes the entire read cache. Called after any successful write. */
export function clearReadCache(): void {
  store.clear();
}

/** Drops cache entries whose key matches the predicate (targeted invalidation). */
export function invalidateRead(predicate: (key: string) => boolean): void {
  for (const k of [...store.keys()]) if (predicate(k)) store.delete(k);
}
